import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";

const AppContext = createContext(null);

// Tempo de inatividade para logout automatico (20 minutos em milissegundos)
const INACTIVITY_TIMEOUT = 20 * 60 * 1000;

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [userType, setUserType] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [companyPlan, setCompanyPlan] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Verifica se o usuario e admin
  const checkIsAdmin = async (userId) => {
    try {
      const { data, error } = await supabase
        .from("admins")
        .select("id")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) {
        return false;
      }
      return !!data;
    } catch (err) {
      return false;
    }
  };

  // Busca o perfil do usuario no banco (tabela mulheres)
  const fetchProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        .from("mulheres")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) {
        return null;
      }
      return data;
    } catch (err) {
      return null;
    }
  };

  // Atualiza perfil no banco (cria se nao existir)
  const updateProfile = async (updates) => {
    if (!user) return { error: "Usuario nao logado" };

    try {
      const { data: existingProfile } = await supabase
        .from("mulheres")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();

      let error;

      if (existingProfile) {
        const result = await supabase
          .from("mulheres")
          .update({
            ...updates,
            updated_at: new Date().toISOString()
          })
          .eq("user_id", user.id);
        error = result.error;
      } else {
        const result = await supabase
          .from("mulheres")
          .insert({
            user_id: user.id,
            email: user.email,
            ...updates,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
        error = result.error;
      }

      if (error) {
        return { error };
      }

      const updatedProfile = {
        ...profile,
        ...updates,
        user_id: user.id,
        email: user.email,
        updated_at: new Date().toISOString()
      };
      setProfile(updatedProfile);
      return { data: updatedProfile };
    } catch (err) {
      return { error: err };
    }
  };

  // Verifica sessao inicial e escuta mudancas de auth
  useEffect(() => {
    let mounted = true;

    // Carrega os dados complementares do usuario.
    // IMPORTANTE: nunca chamar isto de dentro do callback do onAuthStateChange
    // sem adiar a execucao — o cliente Supabase trava esperando a propria trava.
    const carregarDados = async (session) => {
      if (!mounted) return;

      if (session?.user) {
        setUser(session.user);

        const adminStatus = await checkIsAdmin(session.user.id);
        if (!mounted) return;
        setIsAdmin(adminStatus);

        if (!adminStatus) {
          const profileData = await fetchProfile(session.user.id);
          if (!mounted) return;
          setProfile(profileData);
          setUserType(profileData?.user_type || session.user.user_metadata?.user_type || "woman");
        } else {
          setUserType("admin");
        }
      } else {
        setUser(null);
        setProfile(null);
        setUserType(null);
        setIsAdmin(false);
      }

      if (mounted) {
        setLoading(false);
      }
    };

    // 1. Sessao inicial, fora de qualquer callback de auth
    supabase.auth.getSession().then(({ data: { session } }) => {
      carregarDados(session);
    }).catch(() => {
      if (mounted) setLoading(false);
    });

    // 2. Mudancas de autenticacao.
    // O callback precisa retornar IMEDIATAMENTE. Qualquer chamada ao Supabase
    // aqui dentro precisa ser adiada com setTimeout, senao o cliente trava
    // ate a trava interna expirar (o que leva alguns minutos).
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT") {
        if (mounted) {
          setUser(null);
          setProfile(null);
          setUserType(null);
          setIsAdmin(false);
          setCompanyPlan(null);
          setLoading(false);
        }
        return;
      }

      if (event === "SIGNED_IN" || event === "INITIAL_SESSION" || event === "TOKEN_REFRESHED") {
        setTimeout(() => {
          carregarDados(session);
        }, 0);
      }
    });

    // 3. Rede de seguranca: nunca deixa a tela presa em "carregando"
    const timeoutId = setTimeout(() => {
      if (mounted) setLoading(false);
    }, 8000);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      subscription?.unsubscribe();
    };
  }, []);

  // Verifica inatividade
  const checkInactivity = useCallback(() => {
    if (user && Date.now() - lastActivity > INACTIVITY_TIMEOUT) {
      logout();
      alert("Sua sessao expirou por inatividade. Por favor, faca login novamente.");
    }
  }, [user, lastActivity]);

  // Configura verificacao periodica de inatividade
  useEffect(() => {
    if (!user) return;

    const intervalId = setInterval(checkInactivity, 60 * 1000);

    const handleActivity = () => setLastActivity(Date.now());

    const activityEvents = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      clearInterval(intervalId);
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [user, checkInactivity]);

  // Login com email e senha
  const login = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { error };
      }

      return { data };
    } catch (err) {
      return { error: err };
    }
  };

  // Cadastro com email e senha
  const signUp = async (email, password, metadata = {}) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
          emailRedirectTo: "https://www.womom.com.br/login",
        },
      });

      if (error) {
        if (error.message?.includes("email") || error.message?.includes("confirmation")) {
          if (data?.user) {
            return { data };
          }
        }
        return { error };
      }

      return { data };
    } catch (err) {
      return { error: err };
    }
  };

  // Logout
  // Nao limpamos localStorage/sessionStorage na mao: isso deixava a sessao
  // em estado inconsistente. O proprio Supabase cuida disso no signOut.
  const logout = async () => {
    setUser(null);
    setProfile(null);
    setUserType(null);
    setIsAdmin(false);
    setCompanyPlan(null);

    try {
      await supabase.auth.signOut();
    } catch (error) {
      // Ignora erros - o importante e limpar o estado local
    }

    window.location.href = "/";
  };

  // Funcao de compatibilidade para codigo existente
  const updateUser = (newData) => {
    updateProfile(newData);
  };

  return (
    <AppContext.Provider value={{
      user,
      profile,
      userType,
      isAdmin,
      companyPlan,
      setCompanyPlan,
      notifications,
      setNotifications,
      login,
      signUp,
      logout,
      updateUser,
      updateProfile,
      loading,
      supabase,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
